import { FormEvent, useState } from "react";
import { LogIn } from "lucide-react";
import { ApiError, api, type LoginResponse } from "../api";

interface LoginProps {
  onAuthenticated: () => void;
}

export function Login({ onAuthenticated }: LoginProps) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!password) {
      setError("Password is required");
      return;
    }

    setError(null);
    setIsSubmitting(true);
    try {
      await api<LoginResponse>("/api/login", {
        method: "POST",
        body: { password }
      });
      onAuthenticated();
    } catch (requestError) {
      if (requestError instanceof ApiError) {
        setError(requestError.message);
      } else {
        setError("Login failed");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-[#120f0c] px-4 py-10 text-[#f4f0ec]">
      <form
        className="w-full max-w-[380px] rounded-lg border border-[#2b251f] bg-[#08090b] p-6 shadow-[0_24px_80px_rgba(0,0,0,0.42)]"
        onSubmit={handleSubmit}
      >
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-normal text-white">SubMonitor</h1>
          <p className="mt-2 text-sm text-[#9d948c]">Sign in</p>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium text-[#d8d0c8]" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="h-11 w-full rounded-md border border-[#332c25] bg-[#111316] px-3 text-sm text-white outline-none transition focus:border-[#2da8ff] focus:ring-2 focus:ring-[#2da8ff]/25"
            aria-invalid={Boolean(error)}
            aria-describedby={error ? "login-error" : undefined}
          />
        </div>

        {error ? (
          <p id="login-error" role="alert" className="mt-3 text-sm text-[#ff6b5f]">
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          className="mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-md bg-[#178bff] px-4 text-sm font-semibold text-white shadow-[0_10px_30px_rgba(23,139,255,0.24)] transition hover:bg-[#2da8ff] focus:outline-none focus:ring-2 focus:ring-[#2da8ff]/45 disabled:cursor-not-allowed disabled:opacity-65"
          disabled={isSubmitting}
        >
          <LogIn aria-hidden="true" className="h-4 w-4" />
          {isSubmitting ? "Signing in" : "Login"}
        </button>
      </form>
    </main>
  );
}
